import React from 'react';
import {Platform, Pressable, StyleSheet, View} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {observer} from 'mobx-react-lite';

import {useGlobalAlert} from '../../queries/homepage/useGlobalAlert';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import {openPageLink} from '../../utils/navigation';

import AlertMessage, {parseAlertMessage} from './AlertMessage';
import {isGlobalAlertBannerVisible} from './isGlobalAlertBannerVisible';

/**
 * App-wide alert banner (DIS-1457), critical outage scheme (solid red / white
 * text). Mounted once at the App root above the navigator so it pushes content
 * down on every route where it is visible.
 *
 * One unified alert surface across all four brands — brand identity stays in
 * the header beneath. The centred message wraps when long, and inline links
 * (`[label](url)` or bare URLs) render as tappable links. A global alert
 * (`isGlobal`) shows on every route; a non-global alert shows only on the
 * homepage (`globalStore.isHomeRoute`). Persistent — the server controls
 * visibility, so there is no dismiss.
 */

const ALERT_SURFACE = 'rgb(216, 31, 31)';
const ALERT_TEXT = '#FFFFFF';
const ALERT_LINK = '#FFFFFF';

const GlobalAlertBanner: React.FC = observer(() => {
  const alert = useGlobalAlert();
  const {globalStore} = useStores();
  const insets = useSafeAreaInsets();

  if (!isGlobalAlertBannerVisible(alert, globalStore.isHomeRoute)) {
    return null;
  }

  const segments = parseAlertMessage(alert.message);
  const hasLinks = segments.some(segment => segment.type === 'link');
  // Inline links own their own taps; only make the whole strip pressable when
  // there is a pageLink and no inline link to conflict with (avoids a nested
  // press firing navigation twice).
  const isPressable = Boolean(alert.pageLink) && !hasLinks;

  const handlePress = () => {
    if (alert.pageLink) {
      openPageLink(alert.pageLink);
    }
  };

  const containerStyle = [styles.container, Platform.OS !== 'web' && {paddingTop: insets.top + 11}];
  const content = (
    <View style={styles.content}>
      <AlertMessage segments={segments} textStyle={styles.copy} linkStyle={styles.link} />
    </View>
  );

  if (isPressable) {
    return (
      <Pressable testID="global-alert-banner" style={containerStyle} onPress={handlePress}>
        {content}
      </Pressable>
    );
  }

  return (
    <View testID="global-alert-banner" style={containerStyle}>
      {content}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    width: '100%',
    paddingHorizontal: 16,
    paddingVertical: 11,
    backgroundColor: ALERT_SURFACE,
  },
  content: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  copy: {
    flexShrink: 1,
    textAlign: 'center',
    color: ALERT_TEXT,
    fontSize: theme.size.text.body2,
    // Font family intentionally omitted — the themed Text applies the brand
    // family ('Rubik'), which on web is registered across weights. Bold comes
    // from fontWeight, not a 'Rubik-Bold' family (not registered on web).
    fontWeight: '700',
  },
  link: {
    color: ALERT_LINK,
    textDecorationLine: 'underline',
    fontWeight: '700',
  },
});

export default GlobalAlertBanner;
